/**
 * PersonMoviesPage Component
 * Display all movies for a specific person (actor, director, writer)
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  getPersonCredits,
  type PersonActingCredit,
  type PersonCredits,
  type PersonCrewCredit,
} from '../api';
import RatingBadges from './RatingBadges';

export default function PersonMoviesPage() {
  const { personId } = useParams<{ personId: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<PersonCredits | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!personId) return;

      setLoading(true);
      setError(null);

      try {
        const credits = await getPersonCredits(Number(personId));
        setData(credits);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load person');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [personId]);

  if (loading) {
    return (
      <div className="person-page">
        <div className="person-loading">
          <div className="spinner" />
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="person-page">
        <div className="person-error">
          <p>{error || 'Person not found'}</p>
          <button onClick={() => navigate(-1)}>Go Back</button>
        </div>
      </div>
    );
  }

  const { person, acting, directing, writing } = data;

  // Determine primary role for display
  const primaryRole = person.known_for_department || 'Film';

  return (
    <div className="person-page">
      {/* Back Button */}
      <button className="person-back-button" onClick={() => navigate(-1)}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M19 12H5M12 19l-7-7 7-7" />
        </svg>
        Back
      </button>

      {/* Person Header */}
      <div className="person-header">
        <div className="person-avatar">
          {person.profile_url ? (
            <img src={person.profile_url} alt={person.name} />
          ) : (
            <div className="person-avatar-placeholder">{person.name.charAt(0).toUpperCase()}</div>
          )}
        </div>
        <div className="person-info">
          <h1 className="person-name">{person.name}</h1>
          <span className="person-role">{primaryRole}</span>
        </div>
      </div>

      {/* Movie Sections */}
      {directing.length > 0 && <MovieSection title="Director" movies={directing} type="crew" />}

      {writing.length > 0 && <MovieSection title="Writer" movies={writing} type="crew" />}

      {acting.length > 0 && <MovieSection title="Actor" movies={acting} type="cast" />}

      {acting.length === 0 && directing.length === 0 && writing.length === 0 && (
        <div className="person-empty">
          <p>No movie credits found for this person.</p>
        </div>
      )}
    </div>
  );
}

const INITIAL_DISPLAY_LIMIT = 20;

interface MovieSectionProps {
  title: string;
  movies: PersonActingCredit[] | PersonCrewCredit[];
  type: 'cast' | 'crew';
}

function MovieSection({ title, movies }: MovieSectionProps) {
  const [showAll, setShowAll] = useState(false);

  const hasMore = movies.length > INITIAL_DISPLAY_LIMIT;
  const displayedMovies = showAll ? movies : movies.slice(0, INITIAL_DISPLAY_LIMIT);

  return (
    <div className="person-section">
      <h2 className="person-section-title">
        {title}
        <span className="person-section-count">{movies.length}</span>
      </h2>
      <div className="explore-grid">
        {displayedMovies.map((movie) => (
          <Link key={movie.tmdb_id} to={`/movies/${movie.tmdb_id}`} className="explore-movie-card">
            <div className="explore-movie-poster">
              {movie.poster_url ? (
                <img src={movie.poster_url} alt={movie.title} loading="lazy" />
              ) : (
                <div className="explore-movie-poster-placeholder">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4z" />
                  </svg>
                </div>
              )}
              {(movie.is_watched || movie.is_want_to_watch || movie.is_friends_watched) && (
                <div className="explore-movie-badges">
                  {movie.is_watched && (
                    <span className="explore-movie-badge explore-movie-badge-watched">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                        <path d="M20 6L9 17l-5-5" />
                      </svg>
                    </span>
                  )}
                  {movie.is_want_to_watch && !movie.is_watched && (
                    <span className="explore-movie-badge explore-movie-badge-want-to-watch">
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
                      </svg>
                    </span>
                  )}
                  {movie.is_friends_watched && (
                    <span className="explore-movie-badge explore-movie-badge-friend-watched">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                        <circle cx="8.5" cy="7" r="4" />
                        <path d="M20 8v6M23 11h-6" />
                      </svg>
                    </span>
                  )}
                </div>
              )}
              {(movie.imdb_rating || movie.rotten_tomatoes_rating || movie.metacritic_rating) && (
                <div className="explore-movie-rating">
                  <RatingBadges
                    imdbRating={movie.imdb_rating}
                    rottenTomatoesRating={movie.rotten_tomatoes_rating}
                    metacriticRating={movie.metacritic_rating}
                    compact={true}
                  />
                </div>
              )}
            </div>
            <div className="explore-movie-info">
              <h3 className="explore-movie-title">{movie.original_title || movie.title}</h3>
              {movie.year && <span className="explore-movie-year">{movie.year}</span>}
              {'character' in movie && movie.character && (
                <span className="person-movie-character">as {movie.character}</span>
              )}
            </div>
          </Link>
        ))}
      </div>
      {hasMore && (
        <button className="person-show-more-button" onClick={() => setShowAll(!showAll)}>
          {showAll ? 'Show less' : `Show all ${movies.length} movies`}
        </button>
      )}
    </div>
  );
}
